import { Badge } from "@/components/ui/badge";
import { Heart, FileText, DollarSign } from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { getDoctorName } from "@/utils/dataLookups";
import {
  DoctorOrPracticeOwner,
  PatientOnly,
} from "@/components/auth/RoleGuard";

const dispenseStatusColors = {
  pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
  partially_dispensed: "bg-blue-100 text-blue-700 border-blue-200",
  fully_dispensed: "bg-green-100 text-green-700 border-green-200",
  external: "bg-purple-100 text-purple-700 border-purple-200",
};

export default function PrintablePrescription({
  prescription,
  doctors = [],
  associatedClinicalNote = null,
}) {
  return (
    <div className="p-8 bg-white rounded-lg shadow-2xl border printable-area">
      {/* Prescription Header */}
      <header className="flex justify-between items-start pb-6 border-b-2 border-slate-900">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Dr. {getDoctorName(prescription.doctorId, doctors)}
          </h1>
          <p className="text-slate-600">ClubMed Clinic</p>
          <p className="text-slate-600">123 Health St, Wellness City</p>
        </div>
        <div className="text-right">
          <Heart className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="text-2xl font-bold text-slate-800 uppercase tracking-widest mt-2">
            Prescription
          </h2>
        </div>
      </header>

      {/* Patient Info */}
      <section className="grid grid-cols-2 gap-8 my-6 pb-6 border-b">
        <div>
          <h3 className="text-sm font-semibold text-slate-500 uppercase">
            Patient
          </h3>
          <p className="font-bold text-slate-900 text-lg">
            {prescription.patientName ||
              prescription.patientEmail ||
              prescription.patientId}
          </p>
          {/* Add more patient details if available from a patient entity */}
        </div>
        <div className="text-right">
          <p>
            <span className="font-semibold text-slate-600">Date:</span>{" "}
            {formatDate(prescription.prescriptionDate, "MMMM d, yyyy")}
          </p>
          <div className="mt-2">
            <Badge
              className={`${dispenseStatusColors[prescription.dispenseStatus]} border font-medium text-base`}
            >
              {prescription.dispenseStatus?.replace(/_/g, " ")}
            </Badge>
          </div>
        </div>
      </section>

      {/* Medications */}
      <section>
        <h3 className="sr-only">Medications</h3>
        <div className="space-y-6">
          {prescription.medications?.map((item, index) => (
            <div key={index} className="pl-4 border-l-4 border-blue-500">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xl font-bold text-slate-900">
                    {item.medicationName}
                  </p>
                  <p className="text-lg text-slate-700 mt-1">
                    {item.instructions}
                  </p>
                </div>
                {/* Invoice Status Badge */}
                {item.invoicedCount > 0 && (
                  <DoctorOrPracticeOwner>
                    <div className="ml-4 print:hidden">
                      <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
                        <DollarSign className="w-3 h-3" />
                        Invoiced {item.invoicedCount}x
                        {item.totalInvoicedQuantity > 0 &&
                          ` (${item.totalInvoicedQuantity} packs)`}
                      </Badge>
                    </div>
                  </DoctorOrPracticeOwner>
                )}
              </div>
              <div className="flex gap-x-6 text-sm text-slate-600 mt-2">
                {item.quantity && (
                  <span>
                    <strong>Packs/fill:</strong> {item.quantity}
                  </span>
                )}
                {item.dosage && (
                  <span>
                    <strong>Dosage:</strong> {item.dosage}
                  </span>
                )}
                <span>
                  <strong>Freq:</strong> {item.frequency}
                </span>
                <span>
                  <strong>Duration:</strong> {item.duration}
                </span>
                {item.refillsAllowed !== undefined &&
                  item.refillsAllowed > 0 && (
                    <span>
                      <strong>Total fills:</strong> {item.refillsAllowed}
                    </span>
                  )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Associated Clinical Note - For Patients */}
      {associatedClinicalNote && (
        <PatientOnly>
          <section className="mt-8 pt-6 border-t">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-slate-900">
                Related Clinical Note
              </h3>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Visit Date
                  </p>
                  <p className="text-blue-800">
                    {associatedClinicalNote.date
                      ? formatDate(associatedClinicalNote.date, "MMMM d, yyyy")
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-900">Provider</p>
                  <p className="text-blue-800">
                    {getDoctorName(associatedClinicalNote.doctorId, doctors)}
                  </p>
                </div>
              </div>
              {associatedClinicalNote.diagnosis && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-blue-900">Diagnosis</p>
                  <div
                    className="text-blue-800 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: associatedClinicalNote.diagnosis,
                    }}
                  />
                </div>
              )}
              {associatedClinicalNote.treatment_plan && (
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Treatment Plan
                  </p>
                  <div
                    className="text-blue-800 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: associatedClinicalNote.treatment_plan,
                    }}
                  />
                </div>
              )}
            </div>
          </section>
        </PatientOnly>
      )}

      {/* Notes */}
      {prescription.notes && (
        <section className="mt-8 pt-6 border-t">
          <p className="text-slate-700">
            <strong>Pharmacy Notes:</strong> {prescription.notes}
          </p>
        </section>
      )}

      {/* Signature */}
      <footer className="mt-16 text-right">
        <div className="inline-block">
          <p className="font-serif text-lg italic">
            Dr. {getDoctorName(prescription.doctorId, doctors)}
          </p>
          <div className="w-64 h-px bg-slate-900 mt-1"></div>
          <p className="text-sm text-slate-600">Signature</p>
        </div>
      </footer>
    </div>
  );
}
